import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiSearch, FiFilter, FiPlus, FiMoreVertical, FiEdit, FiTrash2, FiUser, FiMail, FiAward, FiSettings, FiArrowLeft, FiLock } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { usersAPI } from '../services/api';
import { getExtensionPlanName } from '../services/extensionAPI';
import { AuthContext } from '../contexts/AuthContext';
import './EndUsers.css';

const EndUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useContext(AuthContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [extensions, setExtensions] = useState([]);
  const [selectedExtension, setSelectedExtension] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [resettingPassword, setResettingPassword] = useState(false);

  // Fetch users from API
  const fetchUsers = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError(null);
      
      // Determine which extensions to fetch based on user role
      let extensionsToFetch = [];
      
      if (currentUser.role === 'superadmin' || currentUser.role === 'admin') {
        // Superadmin/Admin can see all extensions
        const extensionsResponse = await usersAPI.getExtensions();
        if (extensionsResponse.success) {
          extensionsToFetch = extensionsResponse.extensions;
        }
             } else {
         // Partners/Whitelabelers/Resellers can only see extensions they have access to
         const extensionsResponse = await usersAPI.getExtensions();
         if (extensionsResponse.success) {
           // Filter extensions based on user's assigned extensions
           // The assignedExtensions field contains objects with extensionId
           extensionsToFetch = extensionsResponse.extensions.filter(ext => 
             currentUser.assignedExtensions?.some(assignedExt => 
               assignedExt.extensionId === ext._id
             )
           );
         }
       }
      
      if (extensionsToFetch.length === 0) {
        setUsers([]);
        setTotalUsers(0);
        setLoading(false);
        return;
      }
      
      // Fetch users for each accessible extension
      const allUsers = [];
      for (const extension of extensionsToFetch) {
        try {
          const params = {
            page,
            limit: 50, // Get more users per extension
            ...(search && { search })
          };
          
          const response = await usersAPI.getEndUsers(extension._id, params);
          if (response.success && response.users) {
            // Use the populated extension data from backend
            const usersWithExtension = response.users.map(user => ({
              ...user,
              extensionName: user.extensionId?.name, // Use populated data
              extensionId: user.extensionId?._id // Use populated data
            }));
            allUsers.push(...usersWithExtension);
          }
        } catch (err) {
          console.error(`Error fetching users for extension ${extension.name}:`, err);
        }
      }
      
      setUsers(allUsers);
      setTotalUsers(allUsers.length);
      setCurrentPage(1);
      setTotalPages(1); // Since we're loading all users at once
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch extensions
  const fetchExtensions = async () => {
    try {
      const response = await usersAPI.getExtensions();
      if (response.success) {
        setExtensions(response.extensions);
      }
    } catch (err) {
      console.error('Error fetching extensions:', err);
    }
  };

  // Fetch users on component mount and when returning from CreatePartner
  useEffect(() => {
    fetchUsers(currentPage, searchTerm);
    fetchExtensions();
  }, [location.pathname]); // Refresh when navigating back from CreatePartner

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      fetchUsers(1, value);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchUsers(page, searchTerm);
  };

  // Handle user deletion
  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await usersAPI.delete(userId);
        if (response.success) {
          fetchUsers(currentPage, searchTerm);
          alert('User deleted successfully!');
        } else {
          alert(response.message || 'Failed to delete user');
        }
      } catch (err) {
        console.error('Error deleting user:', err);
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Get status badge class
  const getStatusClass = (isActive) => {
    return isActive ? 'active' : 'inactive';
  };

  // Get status text
  const getStatusText = (isActive) => {
    return isActive ? 'Active' : 'Inactive';
  };

  // PlanName component to handle async loading
  const PlanName = ({ user }) => {
    const [planName, setPlanName] = useState('Loading...');
    const [error, setError] = useState(false);

    useEffect(() => {
      const loadPlanName = async () => {
        if (!user.extensionId) {
          setPlanName('N/A');
          return;
        }
        
        try {
          const name = await getExtensionPlanName(user.extensionId, user.plan);
          setPlanName(`${user.plan} - ${name}`);
        } catch (error) {
          console.error('Error getting plan name:', error);
          // Fallback to hardcoded names
          const planNames = ['Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon', 'Zeta'];
          setPlanName(`${user.plan} - ${planNames[user.plan] || 'Unknown'}`);
          setError(true);
        }
      };

      loadPlanName();
    }, [user.extensionId, user.plan]);

    return (
      <span className={`plan-badge ${getPlanBadgeClass(user.plan)}`}>
        {planName}
      </span>
    );
  };

  // Get plan badge class
  const getPlanBadgeClass = (plan) => {
    const planClasses = {
      0: 'plan-free',
      1: 'plan-basic',
      2: 'plan-starter',
      3: 'plan-professional',
      4: 'plan-business',
      5: 'plan-enterprise'
    };
    return planClasses[plan] || 'plan-basic';
  };

  // Handle plan update
  const handlePlanUpdate = async (userId, plan) => {
    setPlanLoading(true);
    try {
      const response = await usersAPI.updatePlan(userId, plan);
      if (response.success) {
        // Refresh users list
        fetchUsers(currentPage, searchTerm);
        setShowPlanModal(false);
        setSelectedUser(null);
        setSelectedPlan(null);
        alert('Plan updated successfully!');
      } else {
        alert(response.message || 'Failed to update plan');
      }
    } catch (err) {
      console.error('Error updating plan:', err);
      alert('Failed to update plan. Please try again.');
    } finally {
      setPlanLoading(false);
    }
  };

  // Open plan modal
  const openPlanModal = (user) => {
    setSelectedUser(user);
    setShowPlanModal(true);
    setSelectedPlan(null);
  };

  // Handle password reset
  const handlePasswordResetClick = (user) => {
    setSelectedUserForPassword(user);
    setShowPasswordResetModal(true);
    setPasswordData({ newPassword: '', confirmPassword: '' });
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    
    try {
      setResettingPassword(true);
      const response = await usersAPI.update(selectedUserForPassword._id, { password: passwordData.newPassword });
      
      if (response.success) {
        toast.success('Password reset successfully');
        setShowPasswordResetModal(false);
        setSelectedUserForPassword(null);
        setPasswordData({ newPassword: '', confirmPassword: '' });
      } else {
        toast.error(response.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleDetailsClick = (user) => {
    navigate(`/users/${user._id}`);
  };

  const handlePlanClick = (user) => {
    openPlanModal(user);
  };

  if (loading && users.length === 0) {
    return (
      <div className="users-page">
        <div className="headerSection">
          <div className="header-content">
            <div className="header-left">
              <h2>All Users</h2>
              <p>Manage all users across all extensions</p>
            </div>
            <button className="back-btn" onClick={() => navigate('/dashboard')}>
              <FiArrowLeft />
              Back to Dashboard
            </button>
          </div>
        </div>
        <div className="dataArea-content">
          <div className="loading">Loading users...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="users-page">
      <div className="headerSection">
        <div className="header-content">
          <div className="header-left">
            <h2>Extension Users</h2>
            <p>Manage users who have access to your extensions</p>
          </div>
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <FiArrowLeft />
            Back to Dashboard
          </button>
        </div>
      </div>

      <div className="dataArea-content">
        <div className="tab">
          <div className="tabHeader d-flex">
            <div className="searchTab">
              <div className="search-gen">
                <input
                  className="form-control"
                  type="search"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
                <span className="bgForm">
                  <FiSearch className="search-ico" />
                </span>
              </div>
              <button 
                className="btn btn-blue btn-curved"
                onClick={() => navigate('/create-user')}
              >
                <FiPlus />
                Add User
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger">
              {error}
            </div>
          )}

          <div className="tabBody">
            <div className="table-responsive">
              <table className="table table-gen">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Assigned Extension</th>
                    <th>Assigned Plan</th>
                    <th>Phone #</th>
                    <th>Status</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user._id}>
                      <td className="table-name">
                        <div className="user-avatar">
                          <FiUser />
                        </div>
                        <div className="nameCellText">
                          <div>{user.name || 'N/A'}</div>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className="extension-badge">
                          {user.extensionId?.name || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <PlanName user={user} />
                      </td>
                      <td>{user.phoneNumber || 'N/A'}</td>
                      <td>
                        <span className={`status-badge ${getStatusClass(user.isActive)}`}>
                          {getStatusText(user.isActive)}
                        </span>
                      </td>
                      <td className="table-date">
                        {formatDate(user.date)}
                      </td>
                      <td>
                        <div className="menu-options">
                          <button className="btnOptions" data-title="Action">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="16" viewBox="0 0 20 16" className="menuTrigger">
                              <g className="a" transform="translate(-2 -4)">
                                <path d="M20,10H4a2,2,0,0,0,0,4H20a2,2,0,0,0,0-4Z"></path>
                                <path d="M4,8H16a2,2,0,0,0,0-4H4A2,2,0,0,0,4,8Z"></path>
                                <path d="M16,16H4a2,2,0,0,0,0,4H16a2,2,0,0,0,0-4Z"></path>
                              </g>
                            </svg>
                          </button>
                          <div className="menu-gen">
                            <button className="btn btn-medium" onClick={() => handlePlanClick(user)}>
                              <figure>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="49 0 450 512.001" className="menuDots" width="22" height="22">
                                  <path d="M400.003,152H256.001c-5.523,0-10,4.477-10,10s4.477,10,10,10h144.002c5.523,0,10-4.477,10-10S405.526,152,400.003,152z" fill="#c4c4c4" data-original="#000000"></path>
                                  <g>
                                    <g xmlns="http://www.w3.org/2000/svg">
                                      <g>
                                        <path d="M365.011,202.931c-1.86-1.86-4.44-2.93-7.07-2.93s-5.21,1.07-7.07,2.93c-1.86,1.86-2.93,4.44-2.93,7.07s1.07,5.21,2.93,7.07s4.44,2.93,7.07,2.93s5.21-1.07,7.07-2.93c1.86-1.86,2.93-4.44,2.93-7.07S366.871,204.791,365.011,202.931z" fill="#c4c4c4" data-original="#000000" className="a"></path>
                                      </g>
                                    </g>
                                    <g xmlns="http://www.w3.org/2000/svg">
                                      <g>
                                        <path d="M263.061,45.931c-1.86-1.86-4.44-2.93-7.07-2.93s-5.21,1.07-7.07,2.93c-1.86,1.86-2.93,4.44-2.93,7.07s1.07,5.21,2.93,7.07c1.86,1.86,4.44,2.93,7.07,2.93s5.21-1.07,7.07-2.93c1.86-1.86,2.93-4.44,2.93-7.07S264.921,47.791,263.061,45.931z" fill="#c4c4c4" data-original="#000000" className="a"></path>
                                      </g>
                                    </g>
                                    <g xmlns="http://www.w3.org/2000/svg">
                                      <g>
                                        <path d="M315.878,200h-59.877c-5.523,0-10,4.477-10,10s4.477,10,10,10h59.877c5.523,0,10-4.477,10-10S321.401,200,315.878,200z" fill="#c4c4c4" data-original="#000000" className="a"></path>
                                      </g>
                                    </g>
                                    <g xmlns="http://www.w3.org/2000/svg">
                                      <g>
                                        <path d="M400.003,260H256.001c-5.523,0-10,4.477-10,10s4.477,10,10,10h144.002c5.523,0,10-4.477,10-10S405.526,260,400.003,260z" fill="#c4c4c4" data-original="#000000" className="a"></path>
                                      </g>
                                    </g>
                                    <g xmlns="http://www.w3.org/2000/svg">
                                      <g>
                                        <path d="M365.011,310.931c-1.86-1.86-4.44-2.93-7.07-2.93s-5.21,1.07-7.07,2.93c-1.86,1.86-2.93,4.44-2.93,7.07s1.07,5.21,2.93,7.07s4.44,2.93,7.07,2.93s5.21-1.07,7.07-2.93c1.86-1.86,2.93-4.44,2.93-7.07S366.871,312.791,365.011,310.931z" fill="#c4c4c4" data-original="#000000" className="a"></path>
                                      </g>
                                    </g>
                                    <g xmlns="http://www.w3.org/2000/svg">
                                      <g>
                                        <path d="M315.878,308h-59.877c-5.523,0-10,4.477-10,10s4.477,10,10,10h59.877c5.523,0,10-4.477,10-10S321.401,308,315.878,308z" fill="#c4c4c4" data-original="#000000" className="a"></path>
                                      </g>
                                    </g>
                                    <g xmlns="http://www.w3.org/2000/svg">
                                      <g>
                                        <path d="M400.003,368H256.001c-5.523,0-10,4.477-10,10s4.477,10,10,10h144.002c5.523,0,10-4.477,10-10S405.526,368,400.003,368z" fill="#c4c4c4" data-original="#000000" className="a"></path>
                                      </g>
                                    </g>
                                    <g xmlns="http://www.w3.org/2000/svg">
                                      <g>
                                        <path d="M365.011,418.931c-1.86-1.86-4.44-2.93-7.07-2.93s-5.21,1.07-7.07,2.93c-1.86,1.86-2.93,4.44-2.93,7.07s1.07,5.21,2.93,7.07s4.44,2.93,7.07,2.93s5.21-1.07,7.07-2.93c1.86-1.86,2.93-4.44,2.93-7.07S366.871,420.791,365.011,418.931z" fill="#c4c4c4" data-original="#000000" className="a"></path>
                                      </g>
                                    </g>
                                    <g xmlns="http://www.w3.org/2000/svg">
                                      <g>
                                        <path d="M315.878,416h-59.877c-5.523,0-10,4.477-10,10s4.477,10,10,10h59.877c5.523,0,10-4.477,10-10S321.401,416,315.878,416z" fill="#c4c4c4" data-original="#000000" className="a"></path>
                                      </g>
                                    </g>
                                    <g xmlns="http://www.w3.org/2000/svg">
                                      <g>
                                        <path d="M419.243,39.001h-76.379C331.823,28.48,316.898,22,300.479,22h-8.76C285.022,8.742,271.263,0,256,0s-29.021,8.742-35.719,22H211.5c-16.419,0-31.343,6.48-42.384,17.001H92.759c-26.885,0-48.758,21.873-48.758,48.758v375.484c0,26.885,21.873,48.758,48.758,48.758h326.483c26.885,0,48.758-21.873,48.758-48.758V87.759C468.001,60.874,446.128,39.001,419.243,39.001z M211.501,42h15.586c4.498,0,8.442-3.003,9.639-7.338C239.111,26.029,247.037,20,256.001,20c8.964,0,16.89,6.029,19.274,14.662c1.197,4.335,5.142,7.338,9.639,7.338h15.565c21.705,0,39.571,16.75,41.354,38.001H170.147C171.93,58.75,189.797,42,211.501,42z M448.001,463.244c0,15.857-12.901,28.758-28.758,28.758H92.759c-15.857,0-28.758-12.901-28.758-28.758V87.759c0-15.857,12.901-28.758,28.758-28.758h62.347c-3.276,7.512-5.105,15.794-5.105,24.5v6.5c0,5.523,4.477,10,10,10H351.98c5.523,0,10-4.477,10-10v-6.5c0-8.705-1.829-16.988-5.105-24.5h62.368c15.857,0,28.758,12.901,28.758,28.758V463.244z" fill="#c4c4c4" data-original="#000000" className="a"></path>
                                      </g>
                                    </g>
                                    <g xmlns="http://www.w3.org/2000/svg">
                                      <g>
                                        <path d="M192.41,149.596c-3.905-3.905-10.237-3.905-14.142-0.001l-42.762,42.763l-13.173-13.174c-3.905-3.904-10.237-3.904-14.143,0c-3.905,3.905-3.905,10.237,0,14.143l20.245,20.245c1.953,1.953,4.512,2.929,7.071,2.929c2.559,0,5.119-0.976,7.071-2.929l49.833-49.833C196.315,159.834,196.315,153.502,192.41,149.596z" fill="#c4c4c4" data-original="#000000" className="a"></path>
                                      </g>
                                    </g>
                                    <g xmlns="http://www.w3.org/2000/svg">
                                      <g>
                                        <path d="M168.001,368h-48c-5.523,0-10,4.477-10,10v48c0,5.523,4.477,10,10,10h48c5.523,0,10-4.477,10-10v-48C178.001,372.477,173.524,368,168.001,368z M158.001,416h-28v-28h28V416z" fill="#c4c4c4" data-original="#000000" className="a"></path>
                                      </g>
                                    </g>
                                    <g xmlns="http://www.w3.org/2000/svg">
                                      <g>
                                        <path d="M168.001,260h-48c-5.523,0-10,4.477-10,10v48c0,5.523,4.477,10,10,10h48c5.523,0,10-4.477,10-10v-48C178.001,264.477,173.524,260,168.001,260z M158.001,308h-28v-28h28V308z" fill="#c4c4c4" data-original="#000000" className="a"></path>
                                      </g>
                                    </g>
                                  </g>
                                </svg>
                              </figure>
                              Plan(s)
                            </button>
                            <button className="btn btn-medium" onClick={() => handleDetailsClick(user)}>
                              <figure>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18.857" height="18" viewBox="0 0 18.857 18" className="menuDots">
                                  <g transform="translate(-1 -1)">
                                    <g transform="translate(1 1)">
                                      <path className="a" d="M326.143,484.286A2.143,2.143,0,1,0,324,482.143,2.143,2.143,0,0,0,326.143,484.286Zm7.286,0a2.143,2.143,0,1,0-2.143-2.143A2.143,2.143,0,0,0,333.429,484.286Zm7.286,0a2.143,2.143,0,1,0-2.143-2.143A2.143,2.143,0,0,0,340.714,484.286Zm-14.571,6.857A2.143,2.143,0,1,0,324,489,2.143,2.143,0,0,0,326.143,491.143Zm7.286,0A2.143,2.143,0,1,0,331.286,489,2.143,2.143,0,0,0,333.429,491.143Zm7.286,0A2.143,2.143,0,1,0,338.571,489,2.143,2.143,0,0,0,340.714,491.143ZM326.143,498A2.143,2.143,0,1,0,324,495.857,2.143,2.143,0,0,0,326.143,498Zm7.286,0a2.143,2.143,0,1,0-2.143-2.143A2.143,2.143,0,0,0,333.429,498Zm7.286,0a2.143,2.143,0,1,0-2.143-2.143A2.143,2.143,0,0,0,340.714,498Z" transform="translate(-324 -480)"></path>
                                    </g>
                                  </g>
                                </svg>
                              </figure>
                              Details
                            </button>
                            <button className="btn btn-medium" onClick={() => handlePasswordResetClick(user)}>
                              <FiLock />
                              Reset Password
                            </button>
                            <button className="btn btn-medium">
                              <figure>
                                <i className="fa fa-exchange" aria-hidden="true"></i>
                              </figure>
                              Assign and dissolve
                            </button>
                            <button className="btn btn-medium">
                              <figure>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 426.66667 426.66667" className="updateWhitelabel">
                                  <g>
                                    <path xmlns="http://www.w3.org/2000/svg" d="m405.332031 192h-170.664062v-170.667969c0-11.773437-9.558594-21.332031-21.335938-21.332031-11.773437 0-21.332031 9.558594-21.332031 21.332031v170.667969h-170.667969c-11.773437 0-21.332031 9.558594-21.332031 21.332031 0 11.777344 9.558594 21.335938 21.332031 21.335938h170.667969v170.664062c0 11.777344 9.558594 21.335938 21.332031 21.335938 11.777344 0 21.335938-9.558594 21.335938-21.335938v-170.664062h170.664062c11.777344 0 21.335938-9.558594 21.335938-21.335938 0-11.773437-9.558594-21.332031-21.335938-21.332031zm0 0"></path>
                                  </g>
                                </svg>
                              </figure>
                              Update Whitelabel Extension
                            </button>
                            <button className="btn btn-medium fontawesomebtn">
                              <i className="fa fa-level-up" aria-hidden="true"></i>
                              Upgrade
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <ul className="pagination">
                <li className={currentPage === 1 ? 'disabled' : ''}>
                  <a onClick={() => handlePageChange(currentPage - 1)}>Previous</a>
                </li>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <li key={page} className={page === currentPage ? 'active' : ''}>
                    <a onClick={() => handlePageChange(page)}>{page}</a>
                  </li>
                ))}
                <li className={currentPage === totalPages ? 'disabled' : ''}>
                  <a onClick={() => handlePageChange(currentPage + 1)}>Next</a>
                </li>
              </ul>
            </div>
          )}

          {users.length === 0 && !loading && (
            <div className="no-data">
              <p>No users found. {searchTerm && 'Try adjusting your search terms.'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Password Reset Modal */}
      {showPasswordResetModal && selectedUserForPassword && (
        <div className="modal-overlay" onClick={() => setShowPasswordResetModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reset Password</h3>
              <button className="modal-close" onClick={() => setShowPasswordResetModal(false)}>×</button>
            </div>
            <form onSubmit={handlePasswordReset} className="password-reset-form">
              <div className="form-group">
                <label>Reset password for: {selectedUserForPassword.email}</label>
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Enter new password (min 6 characters)"
                  required
                  minLength={6}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm new password"
                  required
                  className="form-control"
                />
                {passwordData.confirmPassword && (
                  <div className={`password-match ${passwordData.newPassword === passwordData.confirmPassword ? 'valid' : 'invalid'}`}>
                    {passwordData.newPassword === passwordData.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPasswordResetModal(false);
                    setSelectedUserForPassword(null);
                    setPasswordData({ newPassword: '', confirmPassword: '' });
                  }}
                  disabled={resettingPassword}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={resettingPassword}
                >
                  {resettingPassword ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EndUsers; 